import React from 'react';
import { useSelector } from 'react-redux';
import { Dropdown } from 'antd';
import {
    EyeOutlined,
    EditOutlined,
    SyncOutlined,
    UserAddOutlined,
    PlusOutlined,
    SwapOutlined,
    LinkOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import { selectCurrentAdmin } from '@/redux/auth/selectors';

export default function WorkItemContextMenu({
    children,
    item,
    onView,
    onEdit,
    onStatusChange,
    onAssign,
    onAddSubtask,
    onConvertType,
    onDelete,
}) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    if (!item) return children;

    // Only enable context menu on localhost
    const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
    );

    const userRole = (currentAdmin?.role || '').toLowerCase();
    const isElevated = ['super_admin', 'admin', 'project manager', 'manager'].includes(userRole);
    const isEmployee = userRole === 'employee';
    const isTaskCreator = item.createdBy && String(item.createdBy._id || item.createdBy) === String(currentAdmin?._id);
    const isMilestoneOwner = item.milestoneOwner && String(item.milestoneOwner._id || item.milestoneOwner) === String(currentAdmin?._id);
    const isProjectPM = item.project?.projectManager && String(item.project.projectManager._id || item.project.projectManager) === String(currentAdmin?._id);
    const canManageMilestone = isElevated || isProjectPM || isMilestoneOwner;

    const isMilestone = item.itemType === 'milestone';

    const items = isMilestone
        ? [
            {
                key: 'view',
                icon: <EyeOutlined />,
                label: 'View Milestone',
                onClick: () => onView?.(item),
            },
            {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit Milestone',
                onClick: () => onEdit?.(item),
            },
            {
                key: 'div-1',
                type: 'divider',
            },
            canManageMilestone && {
                key: 'add-task',
                icon: <PlusOutlined />,
                label: 'Add Task',
                onClick: () => onAddSubtask?.(item),
            },
            canManageMilestone && {
                key: 'complete',
                icon: <CheckCircleOutlined className="text-emerald-500" />,
                label: <span className="font-semibold text-emerald-700 dark:text-emerald-400">Mark as Completed</span>,
                onClick: () => onStatusChange?.(item._id, 'Completed'),
            },
            canManageMilestone && {
                key: 'convert',
                icon: <SwapOutlined className="text-amber-500" />,
                label: <span className="font-semibold text-amber-700 dark:text-amber-300">Convert to Task</span>,
                onClick: () => onConvertType?.(item),
            },
            (canManageMilestone || isTaskCreator) && {
                key: 'div-2',
                type: 'divider',
            },
            (canManageMilestone || isTaskCreator) && {
                key: 'delete',
                icon: <DeleteOutlined className="text-red-500" />,
                label: <span className="text-red-600">Archive / Delete</span>,
                onClick: () => onDelete?.(item._id),
            },
        ].filter(Boolean)
        : [
            {
                key: 'view',
                icon: <EyeOutlined />,
                label: 'View Task',
                onClick: () => onView?.(item),
            },
            {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit Task',
                onClick: () => onEdit?.(item),
            },
            {
                key: 'div-1',
                type: 'divider',
            },
            {
                key: 'add-subtask',
                icon: <PlusOutlined />,
                label: 'Create Subtask',
                onClick: () => onAddSubtask?.(item),
            },
            {
                key: 'complete-task',
                icon: <CheckCircleOutlined className="text-emerald-500" />,
                label: <span className="font-semibold text-emerald-700 dark:text-emerald-400">Mark as Completed</span>,
                onClick: () => onStatusChange?.(item._id, 'Completed'),
            },
            !isEmployee && {
                key: 'convert',
                icon: <SwapOutlined className="text-[#03488a]" />,
                label: <span className="font-semibold text-[#03488a] dark:text-[#a7c8ff]">Convert to Milestone</span>,
                onClick: () => onConvertType?.(item),
            },
            (isElevated || isTaskCreator) && {
                key: 'div-2',
                type: 'divider',
            },
            (isElevated || isTaskCreator) && {
                key: 'delete',
                icon: <DeleteOutlined className="text-red-500" />,
                label: <span className="text-red-600">Archive / Delete</span>,
                onClick: () => onDelete?.(item._id),
            },
        ].filter(Boolean);

    // If not on localhost, don't show context menu
    if (!isLocalhost) {
        return <div className="w-full flex items-center">{children}</div>;
    }

    return (
        <Dropdown menu={{ items }} trigger={['contextMenu']}>
            <div className="w-full flex items-center">{children}</div>
        </Dropdown>
    );
}
